import { store } from '../common/redux_store.js'
import * as Action from '../common/redux_actions.js'
import { database } from '../common/dexie_database.js'
import { newDateFromDatestring, getUniqueTemporaryID, objectAssignDeep } from '../common/helpers.js'
import { v1 as uuidv1 } from 'uuid'

/**
 * Race object
 *
 * initial_values: an object containing initial values to populate the Race
 *   with.
 */
export var Race = function Race(initial_values) {

  var self = this;

  if (typeof(initial_values) == 'undefined') {
    initial_values = {};
  }

  // Set up default values.
  this.id = null;
  this.set_temporary_id();
  this.uuid = uuidv1(),
  this.type = 'race';
  this.stages = [];

  this.set_values = function (values) {
    // Seed the new object with the saved values. These values should be what
    // is stored in the database.
    Object.keys(values).forEach(key => {

      let value = values[key];

      if (key == 'stages') {
        for (var index = 0; index < value.length; index++) {
          if (value[index].constructor != RaceStage) {
            value[index] = new RaceStage(value[index]);
          }
        }
      }

      if (key == 'date') {
        if (typeof(value) == 'string') {
          self[key] = newDateFromDatestring(value);
        }
        else if (value && typeof(value.getTime) == 'function') {
          self[key] = new Date(value.getTime());
        }
        else {
          self[key] = value;
        }
      }
      else if (value && typeof(value) == 'object' && value != null && (value.constructor != Array)) {
        self[key] = objectAssignDeep({}, value);
      }
      else {
        self[key] = value;
      }
    });
  }

  this.set_values(initial_values);
}

/**
 * Set a timestamp on this race before saving.
 *
 * By doing this, we can avoid the user interface updating when more than one
 * save operation has occurred.
 */
Race.prototype.setSaveTimestamp = function () {
  this.last_save_timestamp = Date.now();
}

Race.prototype.getDisciplineCategory = function (callback) {
  return 'race';
}

Race.prototype.getDateAsString = function () {
  if (!this.date) {
    return null;
  }
  else if (typeof(this.date) == 'object') {
    return this.date.format('Y-m-d');
  }
  else {
    return String(this.date);
  }
}

Race.prototype.getDistanceUnit = function () {
  if ('prescribed_km' in this) {
    return 'kilometers';
  }
  else if ('prescribed_miles' in this) {
    return 'miles';
  }
  else {
    return xhale_meta_data.distance_unit_preference;
  }
}

Race.prototype.getPoolUnit = function () {
  if ('prescribed_yards' in this || 'prescribed_swim_yards' in this) {
    return 'yards';
  }
  else if ('prescribed_meters' in this || 'prescribed_swim_meters' in this) {
    return 'meters';
  }
  else {
    return xhale_meta_data.pool_unit_preference;
  }
}

Race.prototype.get_overall_time_calculated_from_stages = function () {
  var total_seconds = 0;
  for (var i = 0; i < this.stages.length; i++) {
    total_seconds += this.stages[i].completed_seconds ? this.stages[i].completed_seconds : 0;
  }
  return total_seconds;
}

Race.prototype.has_permanent_id = function (callback) {
  return (!this.id || isNaN(this.id)) ? false : true;
}

Race.prototype.get_overall_time_seconds = function () {
  var overall_time_array = this.overall_time ? this.overall_time.split(':') : [0, 0, 0];
  return (parseInt(overall_time_array[0]) * 3600) + (parseInt(overall_time_array[1]) * 60) + parseInt(overall_time_array[2]);
}

Race.prototype.set_temporary_id = function () {
  var temporary_race_id = getUniqueTemporaryID('race');
  this.id = 'temp-' + temporary_race_id;
  this.temporary_id = temporary_race_id;
}

Race.prototype.copy_plan = function () {
  var copy = new Race({
    'race_name': this.race_name,
    'race_type': this.race_type,
    'race_priority': this.race_priority,
    'taper_days': this.taper_days,
    'attendance_probability': this.attendance_probability,
    'race_url': this.race_url,
    'coach_race_plan': this.coach_plan
  });

  return copy;
}

Race.prototype.collect_save_post_data = function (callback) {
  var self = this;

  var post_data = {}

  var property_names = [
    'athlete_id',
    'group_id',
    'id',
    'temporary_id',
    'uuid',
    'date',
    'race_name',
    'race_type',
    'race_priority',
    'taper_days',
    'attendance_probability',
    'race_url',
    'coach_race_plan',
    'remove_from_bin',
    'training_plan_id',
    'training_plan_day',
  ];

  // Iterate through all the properties and if any have been altered from
  // what we know has been saved, add them to the post_data ready to be
  // saved.
  for (var index in property_names) {
    var property_name = property_names[index];
    post_data[property_name] = self[property_name];

    if (property_name == 'date') {
      if (post_data[property_name] && typeof(post_data[property_name].getTime) == 'function') {
        post_data[property_name] = post_data[property_name].format('Y-m-d');
      }
    }
  }

  return post_data;
}

export var RaceStage = function RaceStage(saved_values) {

  if (typeof(saved_values) == 'undefined') {
    saved_values = {};
  }

  var self = this;

  // Default values.
  this.id = null;
  this.temporary_id = getUniqueTemporaryID('stage');
  this.discipline = null;
  this.advertised_distance_meters = null;
  this.goal_seconds = null;
  this.stage_number = null;
  this.actual_distance_meters = null;
  this.completed_seconds = null;

  this.set_saved_values = function (saved_values) {
    // Seed the new object with the saved values. These values should be what
    // is stored in the database.
    Object.keys(saved_values).forEach(key => {
      self[key] = saved_values[key];
    });
  }

  this.set_saved_values(saved_values);
}

RaceStage.prototype.has_empty_results = function () {
  if (!this.duration_completed && !this.actual_distance_meters) {
    return true;
  }
  else {
    return false;
  }
}

RaceStage.prototype.discipline_category = function () {
  switch (parseInt(this.discipline)) {
    case 1:
      return 'transition';
    case 11:
    case 13:
    case 14:
    case 12:
    case 10:
      return 'swim';
    case 21:
    case 22:
    case 20:
      return 'bike';
    case 31:
    case 33:
    case 32:
    case 30:
      return 'run';
    default:
      return 'other';
  }
}

const RACE_STAGE_TRANSITION = 1;
const RACE_STAGE_SWIM_POOL = 11;
const RACE_STAGE_SWIM_RIVER = 13;
const RACE_STAGE_SWIM_LAKE = 14;
const RACE_STAGE_SWIM_SEA = 12;
const RACE_STAGE_SWIM_OTHER = 10;
const RACE_STAGE_BIKE_ROAD = 21;
const RACE_STAGE_BIKE_OFF_ROAD = 22;
const RACE_STAGE_BIKE_OTHER = 20;
const RACE_STAGE_RUN_ROAD = 31;
const RACE_STAGE_RUN_CROSS_COUNTRY = 33;
const RACE_STAGE_RUN_TRAIL = 34;
const RACE_STAGE_RUN_TRACK = 32;
const RACE_STAGE_RUN_OTHER = 30;
const RACE_STAGE_KAYAK = 42;
const RACE_STAGE_OTHER = 41;

export var RACE_STAGE_DISCIPLINES = {};
RACE_STAGE_DISCIPLINES[RACE_STAGE_TRANSITION] = 'transition';
RACE_STAGE_DISCIPLINES[RACE_STAGE_SWIM_POOL] = 'pool swim';
RACE_STAGE_DISCIPLINES[RACE_STAGE_SWIM_RIVER] = 'river swim';
RACE_STAGE_DISCIPLINES[RACE_STAGE_SWIM_LAKE] = 'lake swim';
RACE_STAGE_DISCIPLINES[RACE_STAGE_SWIM_SEA] = 'sea swim';
RACE_STAGE_DISCIPLINES[RACE_STAGE_SWIM_OTHER] = 'other swim';
RACE_STAGE_DISCIPLINES[RACE_STAGE_BIKE_ROAD] = 'road bike';
RACE_STAGE_DISCIPLINES[RACE_STAGE_BIKE_OFF_ROAD] = 'off-road bike';
RACE_STAGE_DISCIPLINES[RACE_STAGE_BIKE_OTHER] = 'other bike';
RACE_STAGE_DISCIPLINES[RACE_STAGE_RUN_ROAD] = 'road run';
RACE_STAGE_DISCIPLINES[RACE_STAGE_RUN_CROSS_COUNTRY] = 'cross country run';
RACE_STAGE_DISCIPLINES[RACE_STAGE_RUN_TRAIL] = 'trail run';
RACE_STAGE_DISCIPLINES[RACE_STAGE_RUN_TRACK] = 'track run';
RACE_STAGE_DISCIPLINES[RACE_STAGE_RUN_OTHER] = 'other run';
RACE_STAGE_DISCIPLINES[RACE_STAGE_KAYAK] = 'kayak';
RACE_STAGE_DISCIPLINES[RACE_STAGE_OTHER] = 'other';

RaceStage.DISCIPLINE_OPTIONS = {
  'transition': 1,
  'pool swim': 11,
  'river swim': 13,
  'lake swim': 14,
  'sea swim': 12,
  'other swim': 10,
  'road bike': 21,
  'off-road bike': 22,
  'other bike': 20,
  'road run': 31,
  'cross country run': 33,
  'trail run': 34,
  'track run': 32,
  'other run': 30,
  'kayak': 42,
  'other': 41
}

var global_race_id_to_keep;

var raceLoaded = function (new_data) {

  if (xhale.user_preference.offline_mode.is_enabled()) {
    database.transaction("rw", database.races, function() {

      // Add the new data to the database and then send it to the Redux store.
      database.races.put(new_data).then(function () {
        database.races.toArray(function (items) {
          store.dispatch(Action.racesReceive(items));
        })
      });

    }).catch(function (e) {
      store.dispatch(Action.racesReceive([new_data]));
    });
  }
  else {
    store.dispatch(Action.racesReceive([new_data]));
  }

}

var racesLoaded = function (new_data) {

  var data_populated_datetime_string = new Date().toISOString();
  var new_table_metadata = {table_name: 'races', populated_datetime: data_populated_datetime_string };

  if (xhale.user_preference.offline_mode.is_enabled()) {
    database.transaction("rw", database.races, database.table_metadata, function() {

      // Training sessions may have been removed so get rid of these from the store and
      // the database.

      var redux_store_state = store.getState();
      var old_data_race_ids = Object.keys(redux_store_state.main.races);

      // Collect a list of group ids from the recent download.
      var new_data_race_ids = [];
      $.each(new_data.races, function (key, race) {
        new_data_race_ids.push(race.id);
      });

      // Create a list of training session ids to delete which are sessions not listed in
      // the refreshed data.
      var race_ids_to_delete = [];
      for (var i = 0; i < old_data_race_ids.length; i++) {
        if ($.inArray(parseInt(old_data_race_ids[i]), new_data_race_ids) === -1 && parseInt(old_data_race_ids[i]) != global_race_id_to_keep) {

          var old_race_data = redux_store_state.main.race[parseInt(old_data_race_ids[i])];
          race_ids_to_delete.push(parseInt(old_data_race_ids[i]))

        }
      }

      if (race_ids_to_delete.length > 0) {
        // Remove from the database.
        database.races.bulkDelete(race_ids_to_delete);

        // Remove from the Redux store.
        store.dispatch(Action.racesRemove(race_ids_to_delete));
      }

      // Add the new data to the database and then send it to the Redux store.
      database.table_metadata.put(new_table_metadata).then(function () {
        database.races.bulkPut(new_data.races).then(function (last_key) {
          database.races.toArray(function (items) {
            store.dispatch(Action.racesReceive(items));
            store.dispatch(Action.tableMetadataReceive(new_table_metadata));
          })
        });
      });

    }).catch(function (e) {
      store.dispatch(Action.racesReceive(new_data.races));
      store.dispatch(Action.tableMetadataReceive(new_table_metadata));
    });
  }
  else {
    store.dispatch(Action.racesReceive(new_data.races));
    store.dispatch(Action.tableMetadataReceive(new_table_metadata));
  }

}

/**
 * Load full training session data for an individual session.
 */
export var populateIndividualRaceData = function (race_id) {

  // Attempt to load the data from Dexie.
  if (xhale.user_preference.offline_mode.is_enabled()) {

    database.races.toArray(function (items) {

      // Place these items into the Redux store.
      store.dispatch(Action.racesReceive(items));

    }).then(function () {

      // Finished populating store with current database data. Now ajax in new data.
      $.getJSON('/accounts/load-race/' + race_id + '/').done(raceLoaded);

    }).catch(function (e) {
      $.getJSON('/accounts/load-race/' + race_id + '/').done(raceLoaded);
    });

  }
  else {
    // Dispatch groups table metadata to store with null value as obviously no
    // table data exists at all.
    store.dispatch(Action.tableMetadataReceive({table_name: 'races', populated_datetime: null }));
    $.getJSON('/accounts/load-race/' + race_id + '/').done(raceLoaded);
  }

}

export const fetchRacesFromStore = function () {
  // In theory the store only needs to contain sessions relevant to the page
  // whereas the database would contain everything. Still, it may be a good
  // idea to filter it here.
  var store_data = store.getState();
  return store_data.main.races;
}

export const fetchSavedRaceFromStore = function (id) {
  let store_data = store.getState();
  let uuid;
  if (isNaN(id)) {
    uuid = id;
  }
  else {
    uuid = store_data.main.race_uuids_by_id[id];
  }
  let race = null;
  if (uuid in store_data.main.races) {
    race = store_data.main.races[uuid];
  }
  return race;
}

/**
 * Load a list of races.
 */
export var populateRacesData = function () {

  // Attempt to load the data from Dexie.
  if (xhale.user_preference.offline_mode.is_enabled()) {
    database.table_metadata.get('races', function (races_table) {

      var races_table = (races_table) ? races_table : {table_name: 'races', populated_datetime: null};

      database.races.toArray(function (items) {

        // Place these items into the Redux store.
        store.dispatch(Action.racesReceive(items));

      }).then(function () {

        // Dispatch groups table metadata to store.
        store.dispatch(Action.tableMetadataReceive(races_table));

        // Finished populating store with current database data. Now ajax in new data.
        $.getJSON('/accounts/load-races/').done(racesLoaded);

      }).catch(function (e) {
        $.getJSON('/accounts/load-races/').done(racesLoaded);
      });

    });

  }
  else {
    // Dispatch groups table metadata to store with null value as obviously no
    // table data exists at all.
    store.dispatch(Action.tableMetadataReceive({table_name: 'races', populated_datetime: null }));
    $.getJSON('/accounts/load-races/').done(racesLoaded);
  }

}

